import { FUNDRAISING_PRICING_REGISTRY } from '../data/platform-pricing.config';
import type {
  FundingBehavior,
  OptionCondition,
  PlatformOptionSelections,
  PlatformPricingConfiguration,
  ResolvedPlatformPricing,
} from '../models/fundraising-comparison.models';

export class PricingConfigurationResolver {
  enumerateOptionSelections(
    platform: PlatformPricingConfiguration,
  ): PlatformOptionSelections[] {
    const defaults = Object.fromEntries(
      platform.optionGroups.map((group) => [group.id, group.defaultChoiceId]),
    );
    const combinations = platform.optionGroups.reduce<
      PlatformOptionSelections[]
    >(
      (currentCombinations, group) =>
        currentCombinations.flatMap((currentSelections) =>
          (group.choices ?? [{ id: group.defaultChoiceId }]).map((choice) => ({
            ...currentSelections,
            [group.id]: choice.id,
          })),
        ),
      [defaults],
    );
    const seenConfigurations = new Set<string>();

    return combinations.flatMap((combination) => {
      const visibleGroups = platform.optionGroups.filter((group) =>
        this.conditionsMatch(group.visibleWhen, combination),
      );
      const configurationKey = visibleGroups
        .map((group) => `${group.id}:${combination[group.id]}`)
        .join('|');
      if (seenConfigurations.has(configurationKey)) return [];
      seenConfigurations.add(configurationKey);

      return [
        Object.fromEntries(
          platform.optionGroups.map((group) => [
            group.id,
            visibleGroups.includes(group)
              ? combination[group.id]
              : group.defaultChoiceId,
          ]),
        ),
      ];
    });
  }

  resolve(
    platform: PlatformPricingConfiguration,
    requestedSelections: PlatformOptionSelections = {},
  ): ResolvedPlatformPricing {
    const optionSelections = Object.fromEntries(
      platform.optionGroups.map((group) => {
        const requestedChoice = requestedSelections[group.id];
        const choiceId = group.input
          ? this.normalizeInputValue(group, requestedChoice)
          : (group.choices ?? []).some(
                (choice) => choice.id === requestedChoice,
              )
            ? requestedChoice!
            : group.defaultChoiceId;
        return [group.id, choiceId];
      }),
    );

    const optionGroups = platform.optionGroups.filter((group) =>
      this.conditionsMatch(group.visibleWhen, optionSelections),
    );
    const feeGroups = platform.feeGroups.filter((group) =>
      this.conditionsMatch(group.enabledWhen, optionSelections),
    );

    const selectedFundingBehaviors = optionGroups
      .map((group) =>
        (group.choices ?? []).find(
          (choice) => choice.id === optionSelections[group.id],
        ),
      )
      .map((choice) => choice?.fundingBehavior)
      .filter((behavior): behavior is FundingBehavior => Boolean(behavior));

    if (selectedFundingBehaviors.length > 1) {
      throw new Error(
        `${platform.id} resolves to more than one funding behavior.`,
      );
    }

    const selectedChoices = optionGroups.flatMap((group) =>
      (group.choices ?? []).filter(
        (choice) => choice.id === optionSelections[group.id],
      ),
    );
    const evidence =
      selectedChoices.find((choice) => choice.evidence)?.evidence ??
      platform.evidence;
    const regionLabel =
      selectedChoices.find((choice) => choice.regionLabel)?.regionLabel ??
      platform.regionLabel;
    const sourceIds = new Set([
      ...evidence.sourceIds,
      ...(platform.sourceIds ?? []),
      ...platform.publishedFacts.flatMap((fact) => fact.sourceIds),
    ]);
    const sources = FUNDRAISING_PRICING_REGISTRY.sources.filter((source) =>
      sourceIds.has(source.id),
    );

    return {
      platform,
      feeGroups,
      optionGroups,
      optionSelections,
      fundingBehavior:
        selectedFundingBehaviors[0] ??
        platform.defaultFundingBehavior ??
        ({ kind: 'organization-absorbs' } as const),
      evidence,
      regionLabel,
      sources,
    };
  }

  private normalizeInputValue(
    group: PlatformPricingConfiguration['optionGroups'][number],
    requestedValue: string | undefined,
  ): string {
    const input = group.input!;
    const parsedValue = Number(requestedValue);
    if (!Number.isInteger(parsedValue)) return group.defaultChoiceId;
    const clamped = Math.max(
      input.minimumBasisPoints,
      Math.min(input.maximumBasisPoints, parsedValue),
    );
    const stepped =
      input.minimumBasisPoints +
      Math.round((clamped - input.minimumBasisPoints) / input.stepBasisPoints) *
        input.stepBasisPoints;
    return String(
      Math.max(
        input.minimumBasisPoints,
        Math.min(input.maximumBasisPoints, stepped),
      ),
    );
  }

  private conditionsMatch(
    conditions: OptionCondition[] | undefined,
    selections: PlatformOptionSelections,
  ): boolean {
    return (
      !conditions ||
      conditions.every(
        (condition) =>
          selections[condition.optionGroupId] === condition.choiceId,
      )
    );
  }
}
